import { Download, Search, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslations } from '@/hooks/use-translations';

export type CertificationFilter = 'all' | 'passed' | 'failed' | 'pending';
export type AccessFilter = 'all' | 'accessed' | 'not-accessed';
export type CompletionFilter = 'all' | 'completed' | 'not-completed';

interface StatsFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  certificationFilter: CertificationFilter;
  onCertificationFilterChange: (value: CertificationFilter) => void;
  accessFilter: AccessFilter;
  onAccessFilterChange: (value: AccessFilter) => void;
  completionFilter: CompletionFilter;
  onCompletionFilterChange: (value: CompletionFilter) => void;
  onExport: () => void;
  totalCount: number;
  filteredCount: number;
  onClearFilters: () => void;
}

export function StatsFilters({
  searchQuery,
  onSearchChange,
  certificationFilter,
  onCertificationFilterChange,
  accessFilter,
  onAccessFilterChange,
  completionFilter,
  onCompletionFilterChange,
  onExport,
  totalCount,
  filteredCount,
  onClearFilters,
}: StatsFiltersProps) {
  const { t } = useTranslations('common');
  const hasActiveFilters =
    searchQuery !== '' ||
    certificationFilter !== 'all' ||
    accessFilter !== 'all' ||
    completionFilter !== 'all';

  return (
    <div className="space-y-4">
      {/* Search and Export Row */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-livelinx-grey200" />
          <Input
            placeholder={t('stats.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          onClick={onExport}
          className="shrink-0 bg-livelinx-blue300 hover:bg-livelinx-blue400"
        >
          <Download className="mr-2 h-4 w-4" />
          {t('stats.exportCSV')}
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Select value={certificationFilter} onValueChange={onCertificationFilterChange}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('stats.certificationFilter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('stats.allStatuses')}</SelectItem>
            <SelectItem value="completed">{t('stats.passed')}</SelectItem>
            <SelectItem value="failed">{t('stats.failed')}</SelectItem>
            {/* <SelectItem value="pending">{t("stats.pending")}</SelectItem> */}
          </SelectContent>
        </Select>

        <Select value={accessFilter} onValueChange={onAccessFilterChange}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('stats.accessFilter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('stats.allUsers')}</SelectItem>
            <SelectItem value="accessed">{t('stats.accessed')}</SelectItem>
            <SelectItem value="not-accessed">{t('stats.notAccessed')}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={completionFilter} onValueChange={onCompletionFilterChange}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('stats.completionFilter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('stats.allUsers')}</SelectItem>
            <SelectItem value="completed">{t('stats.completed')}</SelectItem>
            <SelectItem value="not-completed">{t('stats.notCompleted')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="outline" onClick={onClearFilters} className="shrink-0">
            <X className="mr-2 h-4 w-4" />
            {t('stats.clearFilters')}
          </Button>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-livelinx-grey200">
        <span>
          {t('stats.showing')} {filteredCount} {t('stats.of')} {totalCount} {t('stats.users')}
        </span>
        {hasActiveFilters && (
          <Badge variant="secondary" className="bg-livelinx-blue50">
            {t('stats.filtersActive')}
          </Badge>
        )}
      </div>
    </div>
  );
}
